/**
 * Mangys LTL Landing — /pages/cotizar-envio
 *
 * Flow:
 *   1. Load current Shopify cart via /cart.js
 *   2. Render cart items + totals in the right sidebar
 *   3. Customer fills address form → click "Cotizar transportistas"
 *   4. POST to backend /api/ltl/estimate → render carrier list
 *   5. Customer picks a carrier → "Pagar pedido" enabled
 *   6. Click "Pagar pedido" → redirect to Shopify checkout with prefilled
 *      shipping_address. Carrier is passed as note attribute so Mangys can
 *      apply the agreed rate after checkout. (Once Carrier Service is
 *      registered in checkout, the rate will appear automatically.)
 *
 * Map: Leaflet + OpenStreetMap tiles (free, no API key).
 */

(function () {
  'use strict';

  const STATE_MAP = {
    'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
    'colorado':'CO','connecticut':'CT','delaware':'DE','florida':'FL','georgia':'GA',
    'hawaii':'HI','idaho':'ID','illinois':'IL','indiana':'IN','iowa':'IA',
    'kansas':'KS','kentucky':'KY','louisiana':'LA','maine':'ME','maryland':'MD',
    'massachusetts':'MA','michigan':'MI','minnesota':'MN','mississippi':'MS','missouri':'MO',
    'montana':'MT','nebraska':'NE','nevada':'NV','new hampshire':'NH','new jersey':'NJ',
    'new mexico':'NM','new york':'NY','north carolina':'NC','north dakota':'ND','ohio':'OH',
    'oklahoma':'OK','oregon':'OR','pennsylvania':'PA','rhode island':'RI','south carolina':'SC',
    'south dakota':'SD','tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT',
    'virginia':'VA','washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY',
    'district of columbia':'DC','puerto rico':'PR',
    'nueva york':'NY','nuevo mexico':'NM','nuevo méxico':'NM','carolina del norte':'NC',
    'carolina del sur':'SC','dakota del norte':'ND','dakota del sur':'SD',
  };

  function normalizeState(raw) {
    const s = (raw || '').toString().trim();
    if (!s) return '';
    if (s.length === 2) return s.toUpperCase();
    return STATE_MAP[s.toLowerCase().replace(/\s+/g, ' ')] || s.toUpperCase();
  }

  function formatMoney(cents) {
    const n = Number(cents) / 100;
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function formatDollars(usd) {
    return '$' + Number(usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  }

  function setLoading(btn, on) {
    if (!btn) return;
    btn.disabled = !!on;
    btn.dataset.loading = on ? 'true' : 'false';
  }

  // ─── State ───────────────────────────────────────────────────────────
  const state = {
    cart: null,
    cartCount: 0,
    cartSubtotalCents: 0,
    quotes: [],
    selectedQuote: null,
    address: null,
  };

  // ─── DOM refs ────────────────────────────────────────────────────────
  const root = document.getElementById('mltl-landing');
  if (!root) return;
  const endpoint = root.dataset.endpoint;
  const originZip = root.dataset.originZip;
  const originLat = parseFloat(root.dataset.originLat);
  const originLon = parseFloat(root.dataset.originLon);

  const $ = (sel) => root.querySelector(sel);
  const cartEmpty   = $('[data-mltl-cart-empty]');
  const cartItemsEl = $('[data-mltl-cart-items]');
  const cartTotals  = $('[data-mltl-cart-totals]');
  const cartSubtotal= $('[data-mltl-cart-subtotal]');
  const cartTotal   = $('[data-mltl-cart-total]');
  const form        = $('[data-mltl-form]');
  const quoteBtn    = $('[data-mltl-quote-btn]');
  const carriersSection = $('[data-mltl-carriers-section]');
  const carriersCount   = $('[data-mltl-carriers-count]');
  const carriersList    = $('[data-mltl-carriers]');
  const message     = $('[data-mltl-message]');
  const sumProducts = $('[data-mltl-summary-products]');
  const sumShipping = $('[data-mltl-summary-shipping]');
  const sumTotal    = $('[data-mltl-summary-total]');
  const sumCarrier  = $('[data-mltl-summary-carrier]');
  const checkoutBtn = $('[data-mltl-checkout]');
  const destLabel   = $('[data-mltl-dest-label]');

  // ─── Map (Leaflet) ───────────────────────────────────────────────────
  let map, originMarker, destMarker, routeLine;
  function initMap() {
    if (typeof L === 'undefined' || map) return;
    const center = [originLat, originLon];
    map = L.map('mltl-map', { scrollWheelZoom: false }).setView(center, 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);
    originMarker = L.marker(center).addTo(map).bindPopup('Mangys Houston · 77092');
  }
  function setDestinationOnMap(lat, lon, label) {
    if (!map) return;
    if (destMarker) destMarker.remove();
    if (routeLine)  routeLine.remove();
    destMarker = L.marker([lat, lon]).addTo(map).bindPopup(label || 'Destino');
    routeLine = L.polyline([[originLat, originLon], [lat, lon]], { color: '#2D7D5A', weight: 3, dashArray: '6,8' }).addTo(map);
    const group = L.featureGroup([originMarker, destMarker, routeLine]);
    map.fitBounds(group.getBounds(), { padding: [40, 40] });
  }
  // Geocode a US ZIP code using free Zippopotam.us API (no key needed)
  async function geocodeZip(zip) {
    try {
      const resp = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
      if (!resp.ok) return null;
      const data = await resp.json();
      const place = (data.places || [])[0];
      if (!place) return null;
      return {
        lat: parseFloat(place.latitude),
        lon: parseFloat(place.longitude),
        label: `${place['place name']}, ${place['state abbreviation']} ${zip}`,
      };
    } catch (_) { return null; }
  }

  // ─── Cart sync ───────────────────────────────────────────────────────
  async function loadCart() {
    try {
      const resp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await resp.json();
      state.cart = cart;
      state.cartCount = cart.item_count;
      state.cartSubtotalCents = cart.items_subtotal_price || cart.total_price || 0;
      renderCart();
      renderSummary();
    } catch (err) {
      console.warn('[mltl] cart load failed', err);
    }
  }

  function renderCart() {
    const cart = state.cart;
    if (!cart || !cart.items || cart.items.length === 0) {
      cartEmpty.hidden = false;
      cartItemsEl.hidden = true;
      cartTotals.hidden = true;
      return;
    }
    cartEmpty.hidden = true;
    cartItemsEl.hidden = false;
    cartTotals.hidden = false;
    cartItemsEl.innerHTML = cart.items.map((it) => `
      <li class="mltl-cart__item">
        ${it.image ? `<img class="mltl-cart__item-img" src="${escapeHtml(it.image)}" alt="" />` : '<div class="mltl-cart__item-img"></div>'}
        <div class="mltl-cart__item-info">
          <p class="mltl-cart__item-title">${escapeHtml(it.product_title || it.title)}</p>
          <p class="mltl-cart__item-meta">Cantidad: ${it.quantity}</p>
        </div>
        <span class="mltl-cart__item-price">${formatMoney(it.line_price)}</span>
      </li>
    `).join('');
    cartSubtotal.textContent = formatMoney(state.cartSubtotalCents);
    cartTotal.textContent = formatMoney(state.cartSubtotalCents);
  }

  // ─── Quote submission ────────────────────────────────────────────────
  form.addEventListener('submit', async function (e) {
    e.preventDefault();
    hideMessage();

    const fd = new FormData(form);
    const address = {
      first_name: (fd.get('first_name') || '').toString().trim(),
      last_name:  (fd.get('last_name') || '').toString().trim(),
      company:    (fd.get('company') || '').toString().trim(),
      address1:   (fd.get('address1') || '').toString().trim(),
      address2:   (fd.get('address2') || '').toString().trim(),
      city:       (fd.get('city') || '').toString().trim(),
      state:      normalizeState(fd.get('state')),
      zip:        (fd.get('zip') || '').toString().trim(),
      phone:      (fd.get('phone') || '').toString().trim(),
    };

    if (!/^\d{5}$/.test(address.zip)) {
      showMessage('Ingresa un código postal de 5 dígitos.', 'error');
      return;
    }
    if (state.cartCount === 0) {
      showMessage('Tu carrito está vacío. Agregá productos antes de cotizar el envío.', 'error');
      return;
    }

    state.address = address;
    setLoading(quoteBtn, true);

    // Geocode in parallel with the quote request
    const geocodePromise = geocodeZip(address.zip);

    try {
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pacaCount: state.cartCount,
          destination: { zip: address.zip, country: 'US', state: address.state },
        }),
      });
      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || !data.qualifies) {
        const msg = data.reason === 'below_minimum'
          ? `Necesitás al menos ${data.minRequired || 5} pacas para envío mayorista.`
          : data.reason === 'non_us_destination'
            ? 'Solo enviamos dentro de Estados Unidos.'
            : 'No pudimos cotizar el envío. Probá de nuevo o contactanos.';
        showMessage(msg, 'error');
        carriersSection.hidden = true;
        return;
      }

      state.quotes = data.quotes || [];
      state.selectedQuote = null;
      renderCarriers();

      // Update map with destination
      const geo = await geocodePromise;
      if (geo) {
        setDestinationOnMap(geo.lat, geo.lon, `${address.city}, ${address.state} ${address.zip}`);
        destLabel.textContent = `${address.city}, ${address.state} ${address.zip}`;
      }
    } catch (err) {
      console.warn('[mltl] quote failed', err);
      showMessage('No pudimos cotizar el envío en este momento. Probá de nuevo.', 'error');
    } finally {
      setLoading(quoteBtn, false);
    }
  });

  function renderCarriers() {
    if (state.quotes.length === 0) {
      carriersSection.hidden = true;
      return;
    }
    carriersSection.hidden = false;
    carriersCount.textContent = `${state.quotes.length} transportista${state.quotes.length === 1 ? '' : 's'} disponible${state.quotes.length === 1 ? '' : 's'}`;
    carriersList.innerHTML = state.quotes.map((q, idx) => `
      <label class="mltl-carrier" data-mltl-carrier-idx="${idx}">
        <span class="mltl-carrier__radio"></span>
        <div class="mltl-carrier__info">
          <p class="mltl-carrier__name">${escapeHtml(q.carrier || 'Transportista LTL')}</p>
          <p class="mltl-carrier__meta">
            ${q.serviceDays ? `${q.serviceDays} ${q.serviceDays === 1 ? 'día hábil' : 'días hábiles'}` : 'Tránsito por confirmar'}
            ${q.deliveryDate ? ` · entrega estimada ${q.deliveryDate.slice(0,10)}` : ''}
          </p>
        </div>
        <span class="mltl-carrier__price">${formatDollars(q.priceUsd)} USD</span>
      </label>
    `).join('');

    carriersList.querySelectorAll('[data-mltl-carrier-idx]').forEach((el) => {
      el.addEventListener('click', () => selectCarrier(parseInt(el.dataset.mltlCarrierIdx, 10)));
    });
  }

  function selectCarrier(idx) {
    state.selectedQuote = state.quotes[idx] || null;
    carriersList.querySelectorAll('.mltl-carrier').forEach((el, i) => {
      el.classList.toggle('is-selected', i === idx);
    });
    renderSummary();
  }

  function renderSummary() {
    const productsUsd = state.cartSubtotalCents / 100;
    sumProducts.textContent = formatMoney(state.cartSubtotalCents);
    if (state.selectedQuote) {
      const shipUsd = parseFloat(state.selectedQuote.priceUsd) || 0;
      sumShipping.textContent = formatDollars(shipUsd) + ' USD';
      sumTotal.textContent = formatDollars(productsUsd + shipUsd);
      sumCarrier.hidden = false;
      sumCarrier.textContent = `Transportista seleccionado: ${state.selectedQuote.carrier} · ${state.selectedQuote.serviceDays || '?'} días hábiles`;
      checkoutBtn.disabled = !state.address || state.cartCount === 0;
    } else {
      sumShipping.textContent = '—';
      sumTotal.textContent = formatMoney(state.cartSubtotalCents);
      sumCarrier.hidden = true;
      checkoutBtn.disabled = true;
    }
  }

  // ─── Checkout ────────────────────────────────────────────────────────
  checkoutBtn.addEventListener('click', function () {
    if (!state.address || !state.selectedQuote) return;
    // Build Shopify checkout permalink with prefilled address.
    // Note: shipping rate cannot be locked in via URL params on Free/Basic plans;
    // it'll be recalculated at checkout. Once Carrier Service is registered the
    // LTL rate will appear there automatically.
    const a = state.address;
    const q = state.selectedQuote;
    const params = new URLSearchParams({
      'checkout[shipping_address][first_name]': a.first_name,
      'checkout[shipping_address][last_name]':  a.last_name,
      'checkout[shipping_address][company]':    a.company,
      'checkout[shipping_address][address1]':   a.address1,
      'checkout[shipping_address][address2]':   a.address2,
      'checkout[shipping_address][city]':       a.city,
      'checkout[shipping_address][province]':   a.state,
      'checkout[shipping_address][zip]':        a.zip,
      'checkout[shipping_address][phone]':      a.phone,
      'checkout[shipping_address][country]':    'US',
      'attributes[ltl_quote_id]':               q.quoteId || '',
      'attributes[ltl_carrier]':                q.carrier || '',
      'attributes[ltl_price_usd]':              q.priceUsd || '',
      'attributes[ltl_service_days]':           q.serviceDays || '',
    });
    window.location.href = '/checkout?' + params.toString();
  });

  // ─── Messages ────────────────────────────────────────────────────────
  function showMessage(text, level) {
    message.hidden = false;
    message.className = 'mltl-message is-' + (level || 'info');
    message.textContent = text;
    message.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  function hideMessage() { message.hidden = true; message.textContent = ''; }

  // ─── Boot ────────────────────────────────────────────────────────────
  function boot() {
    initMap();
    loadCart();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init map when Leaflet finishes loading (loaded with defer)
  window.addEventListener('load', initMap);

  // Re-sync cart if user comes back from another tab
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) loadCart();
  });
})();
